import React, { useState, useEffect, useContext } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import ArgonBox from 'components/ArgonBox';
import CustomInput from 'components/CustomInput';
import { Button } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import ArgonButton from 'components/ArgonButton';
import * as crypto from 'crypto-js';
import Base64 from 'crypto-js/enc-base64url';
import ArgonSelect from 'components/ArgonSelect';
import ArgonTypography from 'components/ArgonTypography';

import { UserContext } from 'context/userContext';
import { useNotifications } from 'context/NotificationContext';

import { saveOrgDetails } from 'services/org';

function OrgSetup({ orgData, onClose, refreshOrgs }) {
  const [org, setOrg] = useState({
    name: '',
    org_id: '',
    org_type: '',
    type: '',
    client_id: '',
    client_secret: '',
    instance_url: '',
    configuration: {},
    salesforce_access_token: '',
    salesforce_refresh_token: '',
  });

  const [errors, setErrors] = useState({});
  const { userId } = useContext(UserContext);
  const { addNotification } = useNotifications();
  const [popupWindow, setPopupWindow] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const typeOptions = [
    { label: 'Developer', value: 'Developer' },
    { label: 'Production', value: 'Production' },
    { label: 'Testing', value: 'Testing' },
  ];

  const platformTypes = [
    { value: 'CPQ', label: 'CPQ' },
    { value: 'RC', label: 'Revenue Cloud' },
    { value: 'OTHER', label: 'Other' },
  ];

  useEffect(() => {
    if (orgData) {
      const initialOrg = {
        name: orgData.name || '',
        org_id: orgData.org_id || '',
        org_type: orgData.org_type || '',
        type: orgData.type || '',
        client_id: orgData.client_id || '',
        client_secret: orgData.client_secret || '',
        instance_url: orgData.instance_url || '',
        configuration: orgData.configuration || {},
        salesforce_access_token: orgData.salesforce_access_token || '',
        salesforce_refresh_token: orgData.salesforce_refresh_token || '',
        _id: orgData._id,
      };
      setOrg(initialOrg);
      setIsConnected(!!initialOrg.salesforce_access_token && !!initialOrg.salesforce_refresh_token);
    } else {
      setOrg({
        name: '',
        org_id: '',
        org_type: '',
        type: '',
        client_id: '',
        client_secret: '',
        instance_url: '',
        configuration: {},
        salesforce_access_token: '',
        salesforce_refresh_token: '',
      });
      setIsConnected(false);
    }
  }, [orgData]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== 'http://localhost:3000' && event.origin !== window.location.origin) {
        console.warn('Message received from unexpected origin:', event.origin);
        return;
      }

      if (event.data.type === 'salesforce-auth') {
        setIsConnecting(false);
        if (event.data.status === 'success') {
          setOrg((prevOrg) => ({
            ...prevOrg,
            org_id: event.data.orgId,
            salesforce_access_token: event.data.accessToken,
            salesforce_refresh_token: event.data.refreshToken,
            instance_url: event.data.instanceUrl,
          }));
          setIsConnected(true);
          addNotification({
            title: ['Salesforce Connected'],
            details: 'Successfully authenticated and connected to Salesforce.',
            severity: 'success',
          });
          localStorage.removeItem('codeVerifier');
          localStorage.removeItem('oauthState');
          if (popupWindow) {
            popupWindow.close();
            setPopupWindow(null);
          }
        } else {
          addNotification({
            title: ['Salesforce Auth Failed'],
            details:
              event.data.error || 'Could not authenticate with Salesforce. Please check credentials or try again.',
            severity: 'error',
          });
          if (popupWindow) {
            popupWindow.close();
            setPopupWindow(null);
          }
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [popupWindow, addNotification]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name.startsWith('configuration.')) {
      const configKey = name.split('.')[1];
      setOrg((prevOrg) => ({
        ...prevOrg,
        configuration: { ...prevOrg.configuration, [configKey]: value },
      }));
    } else {
      setOrg((prevOrg) => ({ ...prevOrg, [name]: value }));
    }
    setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
  };

  const handleConnectToSalesforce = () => {
    let hasErrors = false;
    const newErrors = {};
    if (!org.client_id) {
      newErrors.client_id = 'Client ID is required.';
      hasErrors = true;
    }
    if (!org.client_secret) {
      newErrors.client_secret = 'Client Secret is required.';
      hasErrors = true;
    }
    if (hasErrors) {
      setErrors(newErrors);
      addNotification({
        title: ['Validation Error'],
        details: 'Client ID and Client Secret are required to connect to Salesforce.',
        severity: 'error',
      });
      return;
    }
    setErrors({});
    setIsConnecting(true);
    addNotification({
      title: ['Connecting to Salesforce'],
      details: 'Attempting to authenticate. Please follow instructions in the popup.',
      severity: 'info',
    });
    const state = uuidv4();
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = generateCodeChallenge(codeVerifier);
    localStorage.setItem('codeVerifier', codeVerifier);
    localStorage.setItem('oauthState', state);
    const authUrl = `http://localhost:3000/oauth2/auth?client_id=${org.client_id}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256&code_verifier=${codeVerifier}&client_secret=${org.client_secret}`;
    const newWindow = window.open(authUrl, 'SalesforceLogin', 'width=600,height=600');
    setPopupWindow(newWindow);
    const checkWindowClosed = setInterval(() => {
      if (newWindow && newWindow.closed) {
        if (isConnecting) {
          addNotification({
            title: ['Connection Attempt Ended'],
            details: 'Salesforce connection window was closed before completion.',
            severity: 'warning',
          });
        }
        setIsConnecting(false);
        clearInterval(checkWindowClosed);
        setPopupWindow(null);
      }
    }, 500);
  };

  const handleDisconnect = () => {
    setOrg((prevOrg) => ({
      ...prevOrg,
      salesforce_access_token: '',
      salesforce_refresh_token: '',
      instance_url: '',
      org_id: '',
    }));
    setIsConnected(false);
    addNotification({
      title: ['Salesforce Disconnected'],
      details: 'You have been disconnected. Save changes to persist this.',
      severity: 'info',
    });
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!org.name.trim()) newErrors.name = 'Name is required';
    if (!org.type.trim()) newErrors.type = 'Platform is required';
    if (!org.org_type.trim()) newErrors.org_type = 'Org Type is required';
    if (!org.client_id.trim()) newErrors.client_id = 'Client ID is required';
    if (!org.client_secret.trim()) newErrors.client_secret = 'Client Secret is required';
    if (!isConnected && !orgData?.salesforce_access_token) {
      newErrors.connection = 'Salesforce connection is required before saving.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const errorDetails = Object.values(newErrors).join(' ');
      addNotification({
        title: ['Validation Error'],
        details: `Please correct the following: ${errorDetails}`,
        severity: 'error',
      });
      return;
    }
    addNotification({
      title: ['Saving Organization', org.name],
      details: 'Attempting to save organization details...',
      severity: 'info',
    });
    try {
      if (org._id) {
        delete org._id;
      }

      console.log('ORG SAVE', org);
      const dataToSend = { ...org };

      const savedOrgResponse = await saveOrgDetails(userId, dataToSend);
      if (savedOrgResponse.error) {
        throw new Error(savedOrgResponse.error || 'Unknown error from server.');
      }
      addNotification({
        title: ['Organization Saved', org.name],
        details: 'Organization details were successfully saved.',
        severity: 'success',
      });
      onClose();
      refreshOrgs();
    } catch (error) {
      console.error('Error saving org details:', error);
      addNotification({
        title: ['Save Failed', org.name],
        details: error.message || 'Could not save organization details. Please try again.',
        severity: 'error',
      });
    }
  };

  const maskedToken = org.salesforce_access_token ? '••••••••••' : '';
  const maskedRefreshToken = org.salesforce_refresh_token ? '••••••••••' : '';

  function generateCodeVerifier() {
    return uuidv4() + uuidv4() + uuidv4();
  }
  function generateCodeChallenge(verifier) {
    const hash = crypto.SHA256(verifier).toString(Base64);
    return hash;
  }

  return (
    <ArgonBox p={3} style={{ backgroundColor: '#f8f9fa' }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box ml={2} justifyContent="flex-start">
            <Typography variant="h5">{orgData ? 'Edit Org Configuration' : 'Add New Org'}</Typography>
          </Box>
          <Box justifyContent="flex-end">
            <ArgonButton type="submit" variant="contained" color="primary">
              {orgData ? 'Save Changes' : 'Add Org'}
            </ArgonButton>
            <ArgonButton variant="outlined" color="secondary" onClick={onClose} sx={{ ml: 2 }}>
              Cancel
            </ArgonButton>
          </Box>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              {/* --- Category 1: Basic Org Info --- */}
              <Grid item xs={12}>
                {' '}
                {/* Name - takes full width */}
                <CustomInput
                  label="Name"
                  name="name"
                  value={org.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  fullWidth
                />
              </Grid>

              {/* --- Category 2: Org Classification (Selections) --- */}
              <Grid item xs={12} sm={6} display="flex" alignItems="center">
                <ArgonTypography
                  variant="body2"
                  fontWeight="regular"
                  sx={{ color: 'rgba(0,0,0,0.6)', mr: 1, minWidth: '100px' }}
                >
                  Platform:
                </ArgonTypography>
                <ArgonSelect
                  name="type"
                  placeholder="Select Platform"
                  value={org.type}
                  onChange={handleChange}
                  options={platformTypes}
                  error={!!errors.type}
                  sx={{ flexGrow: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} display="flex" alignItems="center">
                <ArgonTypography
                  variant="body2"
                  fontWeight="regular"
                  sx={{ color: 'rgba(0,0,0,0.6)', mr: 1, minWidth: '100px' }}
                >
                  Org Type:
                </ArgonTypography>
                <ArgonSelect
                  name="org_type"
                  placeholder="Select Org Type"
                  value={org.org_type}
                  onChange={handleChange}
                  options={typeOptions}
                  error={!!errors.org_type}
                  sx={{ flexGrow: 1 }}
                />
              </Grid>

              {/* --- Category 3: Connection Credentials (Inputs) --- */}
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Client ID"
                  name="client_id"
                  value={org.client_id}
                  onChange={handleChange}
                  error={!!errors.client_id}
                  helperText={errors.client_id}
                  type="password"
                  disabled={isConnected}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Client Secret"
                  name="client_secret"
                  value={org.client_secret}
                  onChange={handleChange}
                  error={!!errors.client_secret}
                  helperText={errors.client_secret}
                  type="password"
                  disabled={isConnected}
                  fullWidth
                />
              </Grid>

              {/* --- Category 4: Salesforce Data (Read-only) --- */}
              <Grid item xs={12} sm={6}>
                <CustomInput readOnly label="Org ID (from Salesforce)" name="org_id" value={org.org_id} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Instance URL (from Salesforce)"
                  name="instance_url"
                  value={org.instance_url}
                  readOnly
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Access Token"
                  name="salesforce_access_token"
                  value={maskedToken}
                  readOnly
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomInput
                  label="Refresh Token"
                  name="salesforce_refresh_token"
                  value={maskedRefreshToken}
                  readOnly
                  fullWidth
                />
              </Grid>

              {/* --- Category 5: Action Button --- */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                {isConnected ? (
                  <Button variant="contained" color="error" onClick={handleDisconnect}>
                    Disconnect from Salesforce
                  </Button>
                ) : (
                  <Button variant="contained" color="info" onClick={handleConnectToSalesforce} disabled={isConnecting}>
                    {isConnecting ? 'Connecting...' : 'Connect to Salesforce'}
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>
    </ArgonBox>
  );
}

export default OrgSetup;
